import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsis,
  faPenToSquare,
  faCircleMinus,
  faPlus,
} from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import {
  getAllChargingConnectorsSimplified,
  deleteChargingConnector,
} from "../../api/ChargingConnector";
import { useAuth } from "../../providers/AuthContext";
import CustomDropdown from "../../components/CustomDropdown";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import { useTranslation } from "react-i18next";

import ConnectorStatusBadge from "../../components/connector/ConnectorStatusBadge";
import DataTable from "../../components/DataTable";

const ConnectorsPage = () => {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const [connectors, setConnectors] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const [connectorsLoaded, setConnectorsLoaded] = useState(false);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => {},
  });

  const handleGetConnectors = useCallback(async () => {
    try {
      const accessToken = await getAccessToken();
      const newConnectors = await getAllChargingConnectorsSimplified(accessToken);

      setConnectors(Array.isArray(newConnectors) ? newConnectors : []);
      setConnectorsLoaded(true);
    } catch (error) {
        toast.error(error?.message);
        setConnectorsLoaded(true);
    }
  }, [getAccessToken]);

  useEffect(() => {
    handleGetConnectors();
    const intervalId = setInterval(handleGetConnectors, 10000);
    return () => clearInterval(intervalId);
  }, [handleGetConnectors]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  const handleDeleteConnector = async (chargingConnectorPublicId) => {
    const toastId = toast.loading(t("toast.deleting", { entity: t("entity.connector") }));
    try {
      const accessToken = await getAccessToken();
      await deleteChargingConnector(chargingConnectorPublicId, accessToken);
      await handleGetConnectors();
      toast.success(t("toast.deleted", { entity: t("entity.connector") }), { id: toastId });
    } catch (error) {
        toast.error(error?.message, { id: toastId });
    }
  };

  const columns = useMemo(() => [
    {
      key: "chargingConnectorPublicId",
      header: t("table.connectorId"),
      sortable: true,
      rawCell: true,
      getSortValue: (connector) => connector.chargingConnectorPublicId ?? "",
      render: (connector) => (
        <td
          className="cursor-pointer"
          onClick={() => navigate(`/connector/${connector.chargingConnectorPublicId}`)}
        >
          {connector.chargingConnectorPublicId || ""}
        </td>
      ),
    },
    {
      key: "chargingSiteName",
      header: t("entity.site"),
      sortable: true,
      rawCell: true,
      getSortValue: (connector) => connector.chargingSiteName ?? "",
      render: (connector) => (
        <td
          className="cursor-pointer"
          onClick={() => navigate(`/connector/${connector.chargingConnectorPublicId}`)}
        >
          {connector.chargingSiteName || ""}
        </td>
      ),
    },
    {
      key: "chargingPointName",
      header: t("entity.point"),
      sortable: true,
      rawCell: true,
      getSortValue: (connector) => connector.chargingPointName ?? "",
      render: (connector) => (
        <td
          className="cursor-pointer"
          onClick={() => navigate(`/connector/${connector.chargingConnectorPublicId}`)}
        >
          {connector.chargingPointName || ""}
        </td>
      ),
    },
    {
      key: "chargingConnectorName",
      header: t("entity.connector"),
      sortable: true,
      rawCell: true,
      getSortValue: (connector) => connector.chargingConnectorName ?? "",
      render: (connector) => (
        <td
          className="cursor-pointer"
          onClick={() => navigate(`/connector/${connector.chargingConnectorPublicId}`)}
        >
          {connector.chargingConnectorName || ""}
        </td>
      ),
    },
    {
      key: "chargingConnectorStatus",
      header: t("common.status"),
      sortable: true,
      rawCell: true,
      getSortValue: (connector) => connector.chargingConnectorStatus ?? "",
      render: (connector) => (
        <td
          className="cursor-pointer"
          onClick={() => navigate(`/connector/${connector.chargingConnectorPublicId}`)}
        >
          <ConnectorStatusBadge status={connector.chargingConnectorStatus} />
        </td>
      ),
    },
    {
      key: "actions",
      header: "",
      sortable: false,
      searchable: false,
      rawCell: true,
      render: (connector) => (
        <td className="flex justify-end">
          <CustomDropdown
            isRight={true}
            ContentComponent={() => (
              <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                <button
                  className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                  onClick={() => {
                    navigate(`/connector/${connector.chargingConnectorPublicId}`);
                  }}
                >
                  <FontAwesomeIcon icon={faPenToSquare} className="mr-1" />
                  {t("common.edit")}
                </button>

                <button
                  className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                  onClick={() => {
                    setConfirmState({
                      isOpen: true,
                      title: t("confirm.deleteEntity", {
                        entity: t("entity.connector"),
                        name: connector.chargingConnectorName,
                      }),
                      confirm: () => {
                        handleDeleteConnector(connector.chargingConnectorPublicId);
                      },
                    });
                  }}
                >
                  <FontAwesomeIcon icon={faCircleMinus} className="mr-1" />
                  {t("common.delete")}
                </button>
              </div>
            )}
          >
            <div className="w-8 h-6 flex justify-end bg-red items-center cursor-pointer">
              <FontAwesomeIcon icon={faEllipsis} />
            </div>
          </CustomDropdown>
        </td>
      ),
    },
  ], [handleDeleteConnector, navigate, t]);

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute inset-0 pointer-events-none">
              <LoadingTablePage
                title={t("entity.connectors")}
                loading={!connectorsLoaded}
              />
            </div>

            <div
              className={`${!connectorsLoaded ? "loading" : ""} page-content`}
            >
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">
                  {t("pages.chargingConnectors")}
                </h2>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    navigate(`/connector/create`, {
                      state: { }, // Pass standards to the create page
                    });
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} /> {t("pages.createConnector")}
                </button>
              </div>

              <div className="rounded-md shadow-md w-full p-4">
                <DataTable
                  rows={connectors}
                  columns={columns}
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder={t("search.bySitePointOrConnector")}
                  defaultSortColumn="chargingConnectorName"
                  defaultSortOrder="asc"
                  defaultRowsPerPage={30}
                  rowKey={(connector) => connector.chargingConnectorPublicId}
                  emptyContent={t("common.noEntityFound", { entity: t("entity.connectors") })}
                  emptyColSpan={6}
                />
              </div>
            </div>
          </main>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default ConnectorsPage;

